import React from 'react';
import { Badge, OverlayTrigger, Tooltip } from 'react-bootstrap';
import type { MatchBlocker } from '../../api/generated/model';

// Short label for each blocker code. Stays narrow so the badge fits in
// crowded table cells; full message lives in the hover tooltip.
const SHORT_LABEL: Record<string, string> = {
  payment_method_unspecified: 'Method missing',
  bt_method_indeterminate: 'Rail unknown',
  method_mismatch: 'Method differs',
  no_remittance_signal: 'No remittance',
  remittance_unresolved: 'Remittance ≠ invoice',
  check_number_mismatch: 'Check # differs',
  ambiguous_strong_match: 'Ambiguous',
  amount_mismatch: 'Amount differs',
  ambiguous_duplicate_bank_transaction: 'Duplicate BT',
  trace_number_mismatch: 'Trace # differs',
};

const VARIANT: Record<string, string> = {
  payment_method_unspecified: 'warning',
  bt_method_indeterminate: 'warning',
  method_mismatch: 'danger',
  no_remittance_signal: 'secondary',
  remittance_unresolved: 'secondary',
  check_number_mismatch: 'danger',
  ambiguous_strong_match: 'info',
  amount_mismatch: 'danger',
  ambiguous_duplicate_bank_transaction: 'info',
  trace_number_mismatch: 'danger',
};

interface Props {
  blockers?: MatchBlocker[];
}

// MatchBlockers renders the per-candidate "why didn't this auto-link?"
// reasons as small badges. Each badge's full reason text appears on hover.
// Renders nothing for clean matches (empty / missing list).
const MatchBlockersCell: React.FC<Props> = ({ blockers }) => {
  if (!blockers || blockers.length === 0) return null;
  return (
    <div className="d-flex flex-wrap gap-1">
      {blockers.map((b, i) => {
        const code = b.code || '';
        const label = SHORT_LABEL[code] || code || 'Unknown';
        const variant = VARIANT[code] || 'secondary';
        const text = b.message || code;
        const textBg = variant === 'warning' || variant === 'info' ? 'dark' : undefined;
        return (
          <OverlayTrigger
            key={`${code}:${i}`}
            placement="top"
            overlay={<Tooltip>{text}</Tooltip>}
          >
            <Badge bg={variant} text={textBg} style={{ cursor: 'help' }}>
              {label}
            </Badge>
          </OverlayTrigger>
        );
      })}
    </div>
  );
};

export default MatchBlockersCell;
